import { stringifyQuery } from '@jjj/components';
import { store } from 'src/redux/store';
import { defaultApiVersionPath, getApiHost, getApiUrl } from '../settings';
import request from './request';
import { getAuthData } from './auth';
import { getOrganizationCode } from '../settings/organizationSettings';

function updateUrlWithParams(url, params) {
  let updatedUrl = url;

  // eslint-disable-next-line no-restricted-syntax
  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      updatedUrl = updatedUrl.includes(`:${key}`)
        ? updatedUrl.replace(`:${key}`, params[key])
        : `${updatedUrl}${key}=${params[key]}&`;
    }
  }

  return updatedUrl;
}

function getContentType(fileType) {
  switch (fileType) {
    case 'csv':
      return 'text/csv';
    case 'pdf':
      return 'application/pdf';
    default:
      return 'application/json';
  }
}

function getHeaders(contentType, optionalAcceptType) {
  const type = getContentType(contentType);
  const acceptType = optionalAcceptType || null;
  const auth = getAuthData();
  const isUnifiedLoginEnabled = store?.getState()?.FeatureFlags?.flags?.unified_login_enabled;

  const headers = {
    Accept: acceptType || type,
    'Content-Type': type,
    'OS-Type': 'web',
    ...(!isUnifiedLoginEnabled && { 'ORGANIZATION-CODE': getOrganizationCode() }),
  };

  if (auth?.authToken) {
    headers.Authorization = auth.authToken;

    if (!isUnifiedLoginEnabled) {
      headers.ID = auth.id;
    }
  }

  return headers;
}

function getPath(pathTemplate, id) {
  const pathParams = pathTemplate.split('/');

  if (id) {
    if (Array.isArray(id)) {
      [pathParams[1]] = id;
      [, pathParams[3]] = id;
    } else {
      if (pathParams[2] === ':group_id' || pathParams[2] === ':id') {
        pathParams[2] = id;
      } else if (pathParams[1] === 'sessions' || pathParams[1] === 'subscriptions') {
        pathParams[2] = id;
      } else {
        pathParams[1] = id;
      }

      if (pathParams[3] && pathParams[3].startsWith(':')) {
        return pathParams.slice(0, -1).join('/');
      }
    }

    return pathParams.join('/');
  }

  if (pathParams.length > 1 && pathParams[1].startsWith(':')) {
    return pathParams.slice(0, -1).join('/');
  }

  return pathTemplate;
}

function api(pathTemplate, versionPath = defaultApiVersionPath) {
  this.pathTemplate = pathTemplate;

  this.load = ({ search, id, contentTypeHeader, acceptHeader }) => {
    const query = stringifyQuery(search);

    const path = getPath(this.pathTemplate, id);
    const url = `${getApiUrl(versionPath)}/${path}${query ? `?${query}` : ''}`;

    return request(url, {
      method: 'GET',
      data: {},
      headers: getHeaders(contentTypeHeader, acceptHeader),
    });
  };

  this.get = ({ search }) => {
    const query = stringifyQuery(search);

    return request(`${pathTemplate}/?${query}`, {
      method: 'GET',
      data: {},
      headers: getHeaders(null, null),
    });
  };
  this.post = data =>
    request(this.pathTemplate, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: getHeaders(null, null),
    });
  this.put = data =>
    request(this.pathTemplate, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: getHeaders(null, null),
    });
  this.delete = () => request(this.pathTemplate, { method: 'DELETE', headers: getHeaders(null, null) });

  this.loadItem = (itemId, id) => {
    const path = getPath(this.pathTemplate, id);
    const url = `${getApiUrl(versionPath)}/${path}/${itemId}`;

    return request(url, {
      method: 'GET',
      data: {},
      headers: getHeaders(null, null),
    });
  };

  this.createItem = ({ data, id, acceptHeader, contentTypeHeader, search }) => {
    const query = search ? stringifyQuery(search) : null;
    const path = getPath(this.pathTemplate, id);
    const url = `${getApiUrl(versionPath)}/${path}${query ? `?${query}` : ''}`;

    return request(url, {
      method: 'POST',
      headers: getHeaders(contentTypeHeader, acceptHeader),
      body: JSON.stringify(data),
    });
  };

  this.multipleParamsCreateItem = ({ data, params, search, acceptHeader, contentTypeHeader }) => {
    const query = stringifyQuery(search);
    const url = `${getApiUrl(versionPath)}/${this.pathTemplate}${query ? `?${query}` : ''}`;

    return request(updateUrlWithParams(url, params), {
      method: 'POST',
      headers: getHeaders(contentTypeHeader, acceptHeader),
      body: JSON.stringify(data),
    });
  };

  this.updateItem = ({ data, id, search, acceptHeader, contentTypeHeader }) => {
    const query = stringifyQuery(search);
    const path = getPath(this.pathTemplate, id);
    const url = `${getApiUrl(versionPath)}/${path}${query ? `?${query}` : ''}`;

    return request(url, {
      method: 'PUT',
      headers: getHeaders(contentTypeHeader, acceptHeader),
      body: JSON.stringify(data),
    });
  };

  this.patchItem = ({ data, id, search, acceptHeader, contentTypeHeader }) => {
    const query = stringifyQuery(search);
    const path = getPath(this.pathTemplate, id);
    const url = `${getApiUrl(versionPath)}/${path}${query ? `?${query}` : ''}`;

    return request(url, {
      method: 'PATCH',
      headers: getHeaders(contentTypeHeader, acceptHeader),
      body: JSON.stringify(data),
    });
  };

  this.multiparamsUpdateItem = ({ data, params, search, acceptHeader, contentTypeHeader }) => {
    const query = stringifyQuery(search);
    const url = `${getApiUrl(versionPath)}/${this.pathTemplate}${query ? `?${query}` : ''}`;

    return request(updateUrlWithParams(url, params), {
      method: 'PUT',
      headers: getHeaders(contentTypeHeader, acceptHeader),
      body: JSON.stringify(data),
    });
  };

  this.deleteItem = ({ id, data }) => {
    const path = getPath(this.pathTemplate, id);
    const url = `${getApiUrl(versionPath)}/${path}`;

    return request(url, {
      method: 'DELETE',
      headers: getHeaders(null, null),
      body: data ? JSON.stringify(data) : null,
    });
  };

  this.multipleParamsDeleteItem = ({ data, params }) => {
    const url = `${getApiUrl(versionPath)}/${this.pathTemplate}`;

    const headers = getHeaders(null, null);
    delete headers['Content-Type'];

    return request(updateUrlWithParams(url, params), {
      method: 'DELETE',
      headers: getHeaders(null, null),
      body: data ? JSON.stringify(data) : null,
    });
  };

  this.loadFormData = ({ id }) => {
    const path = getPath(this.pathTemplate, id);

    const url = `${getApiUrl(versionPath)}/${path}/form_data`;

    return request(url, {
      method: 'GET',
      data: {},
      headers: getHeaders(null, null),
    });
  };

  this.sendFormData = ({ data, id }) => {
    const path = getPath(this.pathTemplate, id);
    const url = `${getApiUrl(versionPath)}/${path}`;

    const headers = getHeaders(null, null);
    delete headers['Content-Type'];

    return request(url, {
      method: 'POST',
      headers,
      body: data,
    });
  };

  this.multipleParamsSendFormData = ({ data, params }) => {
    const url = `${getApiUrl(versionPath)}/${this.pathTemplate}`;

    const headers = getHeaders(null, null);
    delete headers['Content-Type'];

    return request(updateUrlWithParams(url, params), {
      method: 'POST',
      headers,
      body: data,
    });
  };

  this.loadImage = () => {
    const path = this.pathTemplate;
    const url = `${getApiHost()}${path}`;

    return request(url, {
      method: 'GET',
      data: {},
      headers: getHeaders(null, null),
    });
  };

  this.downloadFile = ({ id, fileType }) => {
    const path = getPath(this.pathTemplate, id);

    const url = `${getApiUrl(versionPath)}/${path}`;

    return request(url, {
      method: 'GET',
      data: {},
      headers: getHeaders(fileType, null),
    });
  };

  this.multipleParamsDownloadFile = ({ params, fileType }) => {
    const url = `${getApiUrl(versionPath)}/${this.pathTemplate}`;

    return request(updateUrlWithParams(url, params), {
      method: 'GET',
      data: {},
      headers: getHeaders(fileType, null),
    });
  };

  this.multipleParamsGetRequest = data => {
    let url = `${getApiUrl(versionPath)}/${this.pathTemplate}?`;

    // eslint-disable-next-line no-restricted-syntax
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        url = url.includes(`:${key}`) ? url.replace(`:${key}`, data[key]) : `${url}${key}=${data[key]}&`;
      }
    }

    return request(url, {
      method: 'GET',
      data: {},
      headers: getHeaders(null, null),
    });
  };

  this.putRequest = ({ data, id, search, acceptHeader, contentTypeHeader }) => {
    let url = `${getApiUrl(versionPath)}/${this.pathTemplate}?`;

    if (id && url.includes(':id')) {
      url = url.replace(':id', id);
    }

    const query = stringifyQuery(search);

    if (query) {
      url = `${url}&${query}`;
    }

    return request(url, {
      method: 'PUT',
      headers: getHeaders(contentTypeHeader, acceptHeader),
      body: JSON.stringify(data),
    });
  };

  return {
    createItem: this.createItem,
    multipleParamsCreateItem: this.multipleParamsCreateItem,
    delete: this.delete,
    deleteItem: this.deleteItem,
    multipleParamsDeleteItem: this.multipleParamsDeleteItem,
    downloadFile: this.downloadFile,
    executePauseMa: this.executePauseMa,
    executeResumeMa: this.executeResumeMa,
    get: this.get,
    load: this.load,
    loadFormData: this.loadFormData,
    loadImage: this.loadImage,
    loadItem: this.loadItem,
    multipleParamsGetRequest: this.multipleParamsGetRequest,
    multipleParamsDownloadFile: this.multipleParamsDownloadFile,
    patchItem: this.patchItem,
    post: this.post,
    put: this.put,
    putRequest: this.putRequest,
    sendFormData: this.sendFormData,
    multipleParamsSendFormData: this.multipleParamsSendFormData,
    updateItem: this.updateItem,
    multiparamsUpdateItem: this.multiparamsUpdateItem,
  };
}

export function loadPauseReasons(status, versionPath = defaultApiVersionPath) {
  const path = 'matching_engine';
  const url = `${getApiUrl(versionPath)}/${path}/${status}_form_data`;

  return request(url, {
    method: 'GET',
    data: {},
    headers: getHeaders(),
  });
}

export function executeAction({ url, method, search, data, params }) {
  const query = search ? stringifyQuery(search) : '';
  const newUrl = `${getApiHost()}/${url}${query ? `?${query}` : ''}`.replace('//api/', '/api/');

  return request(updateUrlWithParams(newUrl, params), {
    method,
    headers: getHeaders(),
    data: {},
    body: data && data.method !== 'get' ? JSON.stringify(data) : null,
  });
}

function simplePost({ path, id }, url, versionPath = defaultApiVersionPath) {
  const newPath = getPath(path, id);

  return request(`${getApiUrl(versionPath)}/${newPath}/${url}`, {
    method: 'POST',
    headers: getHeaders(),
  });
}

export const resetPassword = data => simplePost(data, 'reset_password');
export const disable = data => simplePost(data, 'disable');
export const enable = data => simplePost(data, 'enable');

export default api;
